import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, ThumbsUp, ThumbsDown, X, HelpCircle } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useTrackFAQView, useRateFAQ } from '../hooks/useFAQ';
import type { FAQItem } from '../types';

interface FAQAccordionProps {
  item: FAQItem;
  isOpen?: boolean;
  onToggle?: () => void;
  highlight?: boolean;
}

export function FAQAccordion({
  item,
  isOpen: controlledIsOpen,
  onToggle,
  highlight = false
}: FAQAccordionProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState<'helpful' | 'not-helpful' | null>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('help');

  const trackView = useTrackFAQView();
  const rateFAQ = useRateFAQ();

  const isOpen = controlledIsOpen ?? internalIsOpen;

  // Scroll into view and open when highlighted
  useEffect(() => {
    if (highlight && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setInternalIsOpen(true);
    }
  }, [highlight]);

  const handleOpen = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalIsOpen(true);
    }
    // Track view when opening
    trackView.mutate(item.id);
  };

  const handleClose = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalIsOpen(false);
    }
  };

  const handleRate = (isHelpful: boolean) => {
    if (hasRated) return;

    rateFAQ.mutate(
      { faqId: item.id, isHelpful },
      {
        onSuccess: () => {
          setHasRated(true);
          setRatingSubmitted(isHelpful ? 'helpful' : 'not-helpful');
        }
      }
    );
  };

  return (
    <>
      {/* Question Item */}
      <div
        ref={itemRef}
        id={item.id}
        className={`glass-effect rounded-[15px] overflow-hidden transition-all
                    ${highlight ? 'ring-2 ring-pp-orange ring-offset-2 ring-offset-pp-dark' : ''}`}
      >
        <button
          onClick={handleOpen}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
        >
          <span className="font-medium text-white pr-4">
            {item.question}
          </span>
          <ChevronRight className="h-5 w-5 text-white/40 flex-shrink-0" />
        </button>
      </div>

      {/* Bottom Sheet Modal for Answer */}
      {isOpen && createPortal(
        <>
          <div className="bottom-sheet-backdrop" onClick={handleClose} />
          <div className="bottom-sheet-container">
            <div className="bottom-sheet-modal">
              {/* Header */}
              <div className="bottom-sheet-header !items-start">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-10 h-10 bg-pp-dark/25 rounded-xl flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-pp-orange" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-white leading-tight">
                      {item.question}
                    </h2>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="flex-shrink-0 w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 ml-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Answer Content */}
              <div className="bottom-sheet-content">
                <div className="glass-effect rounded-[15px] p-4">
                  <p className="text-white/90 whitespace-pre-line leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              </div>

              {/* Footer with Rating */}
              <div className="bottom-sheet-footer !flex-col !gap-2">
                {!hasRated ? (
                  <div className="flex items-center justify-between w-full">
                    <span className="text-sm text-white/70">
                      {t('faq.wasHelpful')}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleRate(true)}
                        disabled={rateFAQ.isPending}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl transition-all bg-white/10 text-white/70 hover:bg-pp-teal/20 hover:text-pp-teal border border-white/20 disabled:opacity-50"
                      >
                        <ThumbsUp className="w-4 h-4" />
                        <span className="text-sm">{t('faq.yes')}</span>
                      </button>
                      <button
                        onClick={() => handleRate(false)}
                        disabled={rateFAQ.isPending}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl transition-all bg-white/10 text-white/70 hover:bg-red-500/20 hover:text-red-400 border border-white/20 disabled:opacity-50"
                      >
                        <ThumbsDown className="w-4 h-4" />
                        <span className="text-sm">{t('faq.no')}</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-white/40 text-center w-full">
                    {ratingSubmitted === 'helpful'
                      ? t('faq.thanksFeedback')
                      : t('faq.thanksImproving')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
